import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Bell, Send, Loader2, ChevronDown, ChevronUp, Slack, Mail, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_LABELS = {
  interested: "Interested",
  question: "Question",
  objection: "Objection",
  not_interested: "Not interested",
  please_stop: "Unsubscribe",
  out_of_office: "Out of office",
};

export default function HotReplyAlertsCard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [supported, setSupported] = useState(["interested"]);
  const [form, setForm] = useState({
    slack_webhook_url: "",
    email_enabled: true,
    email_recipients: "",
    alert_intents: ["interested"],
    enabled: true,
  });
  const [lastTestAt, setLastTestAt] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/campaign-alerts-settings`, { credentials: "include" });
      if (!res.ok) throw new Error("load failed");
      const data = await res.json();
      const s = data.settings || {};
      setForm({
        slack_webhook_url: s.slack_webhook_url === "configured" ? "" : (s.slack_webhook_url || ""),
        email_enabled: s.email_enabled !== false,
        email_recipients: (s.email_recipients || []).join(", "),
        alert_intents: s.alert_intents || ["interested"],
        enabled: s.enabled !== false,
      });
      setSupported(data.supported_intents || []);
      setLastTestAt(s.last_test_at || null);
    } catch (e) {
      // silent — card may be hidden for non-admins
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const toggleIntent = (intent) => {
    setForm((f) => ({
      ...f,
      alert_intents: f.alert_intents.includes(intent)
        ? f.alert_intents.filter((i) => i !== intent)
        : [...f.alert_intents, intent],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        slack_webhook_url: form.slack_webhook_url.trim(),
        email_enabled: form.email_enabled,
        email_recipients: form.email_recipients
          .split(/[,;\n]/).map((s) => s.trim()).filter(Boolean),
        alert_intents: form.alert_intents.length ? form.alert_intents : ["interested"],
        enabled: form.enabled,
      };
      const res = await fetch(`${API}/campaign-alerts-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      toast.success("Alert settings saved");
      fetchSettings();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      // Save first so the test uses the latest form values
      await save();
      const res = await fetch(`${API}/campaign-alerts-settings/test`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Test failed");
      const r = data.report || {};
      const slackMsg = r.slack
        ? (r.slack.ok ? "Slack: delivered" : `Slack: ${r.slack.error}`)
        : "Slack: not configured";
      const emailMsg = r.email
        ? (r.email.ok
            ? `Email: sent to ${(r.email.results || []).filter(x=>x.ok).length} recipient(s)`
            : `Email: ${r.email.error || "failed"}`)
        : "Email: disabled";
      const okAny = (r.slack && r.slack.ok) || (r.email && r.email.ok);
      (okAny ? toast.success : toast.error)(`${slackMsg} · ${emailMsg}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  const statusBadge = () => {
    if (!form.enabled) return <Badge className="bg-slate-100 text-slate-600 text-[10px]">Off</Badge>;
    const channels = [];
    if (form.slack_webhook_url) channels.push("Slack");
    if (form.email_enabled) channels.push("Email");
    if (channels.length === 0) return <Badge className="bg-amber-100 text-amber-700 text-[10px]">No channel</Badge>;
    return <Badge className="bg-green-100 text-green-700 text-[10px]">{channels.join(" + ")}</Badge>;
  };

  return (
    <Card className="border-slate-200" data-testid="hot-reply-alerts-card">
      <CardHeader
        className="pb-3 cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
        data-testid="hot-reply-alerts-toggle"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-green-50 border border-green-200 flex items-center justify-center">
              <Flame className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                Hot Reply Alerts {statusBadge()}
              </CardTitle>
              <CardDescription className="text-xs">
                Ping your reps on Slack + email the instant GPT flags a reply.
              </CardDescription>
            </div>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </CardHeader>

      {open && (
        <CardContent className="space-y-5 pt-1">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {/* Master toggle */}
              <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-slate-50">
                <div>
                  <Label className="text-sm font-semibold">Alerts enabled</Label>
                  <p className="text-xs text-slate-500">Master switch for all hot-reply notifications.</p>
                </div>
                <Switch
                  checked={form.enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                  data-testid="alerts-enabled-switch"
                />
              </div>

              {/* Slack */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-semibold">
                  <Slack className="w-4 h-4 text-[#4a154b]" /> Slack Incoming Webhook
                </Label>
                <Input
                  placeholder="https://hooks.slack.com/services/T0…/B0…/XXXX"
                  value={form.slack_webhook_url}
                  onChange={(e) => setForm((f) => ({ ...f, slack_webhook_url: e.target.value }))}
                  className="font-mono text-xs"
                  data-testid="slack-webhook-input"
                />
                <p className="text-xs text-slate-500">
                  In Slack: <strong>Apps → Incoming Webhooks → Add to Slack</strong>, pick a channel,
                  copy the URL, paste it here. Leave blank to disable Slack alerts.
                </p>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm font-semibold">
                    <Mail className="w-4 h-4 text-blue-600" /> Email alerts
                  </Label>
                  <Switch
                    checked={form.email_enabled}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, email_enabled: v }))}
                    data-testid="email-enabled-switch"
                  />
                </div>
                {form.email_enabled && (
                  <>
                    <Input
                      placeholder="manager@acme.com, owner@acme.com (optional extras)"
                      value={form.email_recipients}
                      onChange={(e) => setForm((f) => ({ ...f, email_recipients: e.target.value }))}
                      data-testid="email-recipients-input"
                    />
                    <p className="text-xs text-slate-500">
                      The campaign&apos;s rep is always alerted. Add comma-separated extras above
                      (e.g. manager, sales ops).
                    </p>
                  </>
                )}
              </div>

              {/* Intents */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Trigger on intents</Label>
                <div className="flex flex-wrap gap-2">
                  {supported.map((intent) => {
                    const selected = form.alert_intents.includes(intent);
                    return (
                      <button
                        key={intent}
                        type="button"
                        onClick={() => toggleIntent(intent)}
                        data-testid={`intent-chip-${intent}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                          selected
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {INTENT_LABELS[intent] || intent}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  Recommended: just <strong>Interested</strong> to keep noise low.
                </p>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-100 gap-2 flex-wrap">
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Bell className="w-3.5 h-3.5" />
                  {lastTestAt
                    ? `Last test: ${new Date(lastTestAt).toLocaleString()}`
                    : "No test sent yet"}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendTest}
                    disabled={testing || saving}
                    data-testid="send-test-alert-btn"
                  >
                    {testing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
                    Send test alert
                  </Button>
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving}
                    data-testid="save-alerts-btn"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
                    Save
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
